import type { ReactNode } from "react";
import type { RouteObject } from "react-router-dom";
import { Navigate } from "react-router-dom";
import NotFound from "@/pages/NotFound";
import NoPermission from "@/pages/NoPermission";
import Home from "@/pages/home/page";
import ProductDetail from "@/pages/product/page";
import Cart from "@/pages/cart/page";
import Orders from "@/pages/orders/page";
import OrderDetail from "@/pages/order/page";
import Login from "@/pages/login/page";
import ProxyOrder from "@/pages/proxy-order/page";
import Reports from "@/pages/reports/page";
import FrequentPurchase from "@/pages/frequent-purchase/page";
import WorkApproval from "@/pages/work-approval/page";
import AfterSales from "@/pages/after-sales/page";
import AuthGuard, { GuestGuard, DivideGuard, LimitQuotaGuard } from "./AuthGuard";
import PurchasePlan from "@/pages/purchase-plan/page";
import PurchasePlanDetail from "@/pages/purchase-plan/detail";
import PurchasePlanCreate from "@/pages/purchase-plan/create";
import PurchasePlanThisMonth from "@/pages/purchase-plan/thismonth";
import PurchasePlanMonthDetails from "@/pages/purchase-plan/month-details";
import PurchasePlanDepartmentSubmitStatus from "@/pages/purchase-plan/department-submit-status";
import { isLoggedIn } from "@/utils/auth";

/** 域名根路径：已登录进物资首页，否则进登录页（避免先渲染业务壳再鉴权） */
function RootRedirect() {
  return <Navigate to={isLoggedIn() ? "/goods" : "/login"} replace />;
}

function withAuth(element: ReactNode) {
  return <AuthGuard>{element}</AuthGuard>;
}

function withDivideAuth(element: ReactNode) {
  return (
    <AuthGuard>
      <DivideGuard>{element}</DivideGuard>
    </AuthGuard>
  );
}

function withLimitQuotaAuth(element: ReactNode) {
  return (
    <AuthGuard>
      <LimitQuotaGuard>{element}</LimitQuotaGuard>
    </AuthGuard>
  );
}

const routes: RouteObject[] = [
  {
    path: "/",
    element: <RootRedirect />,
  },
  {
    path: "/login",
    element: (
      <GuestGuard>
        <Login />
      </GuestGuard>
    ),
  },
  {
    path: "/goods",
    element: withAuth(<Home />),
  },
  {
    path: "/product/:id",
    element: withAuth(<ProductDetail />),
  },
  {
    path: "/cart",
    element: withAuth(<Cart />),
  },
  {
    path: "/orders",
    element: withAuth(<Orders />),
  },
  {
    path: "/order/:id",
    element: withAuth(<OrderDetail />),
  },
  {
    path: "/proxy-order",
    element: withDivideAuth(<ProxyOrder />),
  },
  {
    path: "/reports",
    element: withAuth(<Reports />),
  },
  {
    path: "/frequent-purchase",
    element: withAuth(<FrequentPurchase />),
  },
  {
    path: "/work-approval",
    element: withAuth(<WorkApproval />),
  },
  {
    path: "/purchase-plan",
    element: withLimitQuotaAuth(<PurchasePlan />),
  },
  {
    path: "/purchase-plan/create",
    element: withLimitQuotaAuth(<PurchasePlanCreate />),
  },
  {
    path: "/purchase-plan/thismonth",
    element: withLimitQuotaAuth(<PurchasePlanThisMonth />),
  },
  {
    path: "/purchase-plan/month-details",
    element: withLimitQuotaAuth(<PurchasePlanMonthDetails />),
  },
  {
    path: "/purchase-plan/department-submit-status",
    element: withLimitQuotaAuth(<PurchasePlanDepartmentSubmitStatus />),
  },
  {
    path: "/purchase-plan/:id",
    element: withLimitQuotaAuth(<PurchasePlanDetail />),
  },
  {
    path: "/after-sales",
    element: withAuth(<AfterSales />),
  },
  {
    path: "/403",
    element: withAuth(<NoPermission />),
  },
  {
    path: "/404",
    element: <NotFound />,
  },
];

export default routes;
